/**
 * The shared pricing block used by BOTH the homepage pricing section and the
 * /pricing page: Monthly/Yearly toggle (Yearly default), Free / Pro / Team
 * cards (AR 3:4), extra-seat note, money-back guarantee, and, while billing is
 * not live, the founding-member waitlist.
 *
 * CTA behaviour comes from the pure `resolvePlanCta` (billing switch +
 * viewer). Anonymous buyers keep their intent through sign-up
 * (`billing-intent.ts`) and land straight in Stripe Checkout afterwards.
 */
import { useConvex } from 'convex/react'
import type { ConvexReactClient } from 'convex/react'
import { Sparkles } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { trackEvent } from '../../lib/analytics'
import {
  parseIntentSearch,
  saveCheckoutIntent,
  signUpForCheckoutPath,
  takeCheckoutIntent,
} from '../../lib/billing-intent'
import type { CheckoutIntent } from '../../lib/billing-intent'
import { useTranslation } from '../../lib/i18n'
import { resolvePlanCta } from '../../lib/pricing-cta'
import {
  DEFAULT_BILLING_INTERVAL,
  PLANS,
  PLAN_IDS,
  TEAM_MAX_SEATS,
  extraSeatPricePerMonth,
  formatPrice,
} from '../../lib/pricing'
import type { BillingInterval, PaidPlanId, PlanId } from '../../lib/pricing'
import {
  useBillingActions,
  useBillingStatus,
  useViewerEntitlement,
} from '../../lib/use-billing'
import type {
  BillingErrorCode,
  ViewerEntitlement,
} from '../../lib/use-billing'
import { BillingToggle } from './billing-toggle'
import { FOUNDING_ANCHOR_ID, FoundingWaitlist } from './founding-waitlist'
import { MoneyBackGuarantee } from './money-back-guarantee'
import { PlanCard } from './plan-card'

export type PricingSurface = 'pricing_page' | 'home_pricing'

function clampSeats(seats: number) {
  return Math.min(
    TEAM_MAX_SEATS,
    Math.max(PLANS.team.seatsIncluded, Math.floor(seats)),
  )
}

function focusWaitlist() {
  const panel = document.getElementById(FOUNDING_ANCHOR_ID)
  if (!panel) return
  const reduced =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (typeof panel.scrollIntoView === 'function') {
    panel.scrollIntoView({
      behavior: reduced ? 'auto' : 'smooth',
      block: 'start',
    })
  }
  panel.querySelector<HTMLInputElement>('input[type="email"]')?.focus({
    preventScroll: true,
  })
}

type PricingPlansProps = {
  surface: PricingSurface
  planHeading?: 'h2' | 'h3'
}

type BillingActions = ReturnType<typeof useBillingActions>

/** Everything the view needs from Convex/Clerk, injected by the wrapper. */
type PricingBilling = {
  live: boolean | null
  viewer: ViewerEntitlement
  actions: BillingActions
  /** False when there is no backend to store a founding-member signup. */
  waitlistEnabled: boolean
}

const noop = async () => {}

/**
 * Billing state when no Convex client is mounted (unit tests, a build without
 * `VITE_CONVEX_URL`): the same markup as the "billing unknown" first paint,
 * with inert CTAs and no waitlist form. Never throws for a missing provider.
 */
const STATIC_BILLING: PricingBilling = {
  live: null,
  viewer: {
    signedIn: false,
    ready: false,
    isPro: false,
    plan: 'free',
    source: 'none',
  },
  actions: {
    startCheckout: noop,
    openPortal: noop,
    pending: null,
    error: null,
    clearError: () => {},
  },
  waitlistEnabled: false,
}

export function PricingPlans(props: PricingPlansProps) {
  // `Providers` mounts Convex (and Clerk) together; without a client the
  // Convex/Clerk hooks would throw, so render the static view instead.
  // `useConvex` is typed non-null but returns undefined outside a provider.
  const convex = useConvex() as ConvexReactClient | undefined
  return convex ? (
    <ConnectedPricingPlans {...props} />
  ) : (
    <PricingPlansView {...props} billing={STATIC_BILLING} />
  )
}

function ConnectedPricingPlans(props: PricingPlansProps) {
  const { locale } = useTranslation()
  const live = useBillingStatus()
  const viewer = useViewerEntitlement()
  const actions = useBillingActions(locale)
  return (
    <PricingPlansView
      {...props}
      billing={{ live, viewer, actions, waitlistEnabled: true }}
    />
  )
}

function PricingPlansView({
  surface,
  planHeading = 'h3',
  billing,
}: PricingPlansProps & { billing: PricingBilling }) {
  const { locale, t } = useTranslation()
  const { live, viewer, actions, waitlistEnabled } = billing
  const [interval, setIntervalState] = useState<BillingInterval>(
    DEFAULT_BILLING_INTERVAL,
  )
  const [teamSeats, setTeamSeats] = useState(PLANS.team.seatsIncluded)
  const [waitlistPlan, setWaitlistPlan] = useState<PaidPlanId>('pro')
  const [pendingPlan, setPendingPlan] = useState<PlanId | null>(null)
  const [canceled, setCanceled] = useState(false)
  const viewedRef = useRef(false)
  const resumedRef = useRef(false)

  // One pricing_viewed per mount, once the billing mode is known (or 3s).
  useEffect(() => {
    if (viewedRef.current) return
    const emit = () => {
      if (viewedRef.current) return
      viewedRef.current = true
      trackEvent('pricing_viewed', { surface, billing_live: live })
    }
    if (live !== null) {
      emit()
      return
    }
    const timer = window.setTimeout(emit, 3000)
    return () => window.clearTimeout(timer)
  }, [live, surface])

  // Pricing page: restore an intent's plan/interval and the "canceled" note.
  useEffect(() => {
    if (surface !== 'pricing_page') return
    const params = new URLSearchParams(window.location.search)
    if (params.get('checkout') === 'canceled') setCanceled(true)
    const intent = parseIntentSearch(window.location.search)
    if (intent) {
      setIntervalState(intent.interval)
      if (intent.plan === 'team' && intent.seats) {
        setTeamSeats(clampSeats(intent.seats))
      }
    }
  }, [surface])

  // Pricing page: resume a pre-sign-up checkout intent exactly once.
  useEffect(() => {
    if (surface !== 'pricing_page' || resumedRef.current) return
    if (live !== true || !viewer.signedIn || !viewer.ready) return
    resumedRef.current = true
    const intent = takeCheckoutIntent(window.location.search)
    if (!intent) return
    window.history.replaceState(null, '', window.location.pathname)
    const action = resolvePlanCta({ planId: intent.plan, live, viewer })
    if (action === 'checkout') {
      setPendingPlan(intent.plan)
      void actions.startCheckout(intent, surface)
    }
  }, [actions, live, surface, viewer])

  // Clear the per-card spinner when a request fails.
  useEffect(() => {
    if (actions.pending === null) setPendingPlan(null)
  }, [actions.pending])

  function changeInterval(next: BillingInterval) {
    if (next === interval) return
    setIntervalState(next)
    trackEvent('plan_toggled', { surface, interval: next })
  }

  function intentFor(planId: PaidPlanId): CheckoutIntent {
    return planId === 'team'
      ? { plan: 'team', interval, seats: teamSeats }
      : { plan: 'pro', interval }
  }

  return (
    <div className="pricing-plans" data-billing-live={String(live)}>
      <div className="pricing-plans-controls">
        <BillingToggle value={interval} onChange={changeInterval} />
        {/* Rendered (invisible) while the billing mode is still unknown, so
            the plan grid does not jump down when the note appears (CLS, M6).
            Returning visitors whose last known mode is live never reserve
            the space (`html[data-billing='live']`, set pre-paint). */}
        {live !== true ? (
          <p
            className="pricing-early-note"
            data-pending={live === null ? 'true' : undefined}
            aria-hidden={live === null ? true : undefined}
          >
            <Sparkles size={14} aria-hidden="true" />
            {t('pricing.earlyAccessNote')}
          </p>
        ) : null}
      </div>

      {canceled ? (
        <p className="pricing-notice" role="status">
          {t('pricing.canceledNotice')}
        </p>
      ) : null}
      {actions.error ? (
        <p className="pricing-notice is-error" role="alert">
          {errorMessage(actions.error, t)}
        </p>
      ) : null}

      <div className="plan-grid">
        {PLAN_IDS.map((planId) => {
          const action = resolvePlanCta({ planId, live, viewer })
          const paid = planId === 'free' ? null : planId
          return (
            <PlanCard
              key={planId}
              planId={planId}
              interval={interval}
              action={action}
              headingLevel={planHeading}
              pending={pendingPlan === planId && actions.pending !== null}
              teamSeats={teamSeats}
              onTeamSeatsChange={(seats) => setTeamSeats(clampSeats(seats))}
              signupHref={
                paid ? signUpForCheckoutPath(locale, intentFor(paid)) : '#'
              }
              onSignupClick={() => {
                trackEvent('plan_cta_clicked', {
                  surface,
                  plan: planId,
                  interval,
                  action: paid ? 'signup' : 'start_free',
                })
                if (paid) saveCheckoutIntent(intentFor(paid))
              }}
              onAction={() => {
                if (!paid) return
                if (action === 'waitlist') {
                  trackEvent('plan_cta_clicked', {
                    surface,
                    plan: paid,
                    interval,
                    action: 'waitlist',
                  })
                  setWaitlistPlan(paid)
                  focusWaitlist()
                  return
                }
                if (action === 'checkout') {
                  trackEvent('plan_cta_clicked', {
                    surface,
                    plan: paid,
                    interval,
                    action: 'checkout',
                  })
                  setPendingPlan(paid)
                  void actions.startCheckout(intentFor(paid), surface)
                  return
                }
                if (action === 'manage' || action === 'switch') {
                  trackEvent('plan_cta_clicked', {
                    surface,
                    plan: paid,
                    interval,
                    action: 'portal',
                  })
                  setPendingPlan(paid)
                  void actions.openPortal()
                }
              }}
            />
          )
        })}
      </div>

      <div className="pricing-plans-footer">
        <p className="pricing-extra-usage">
          <strong>{t('pricing.extraUsageLabel')}</strong>{' '}
          {t('pricing.extraUsageText', {
            price: formatPrice(extraSeatPricePerMonth(), locale),
          })}
        </p>
        <MoneyBackGuarantee />
      </div>

      {live === true || !waitlistEnabled ? null : (
        <FoundingWaitlist
          plan={waitlistPlan}
          onPlanChange={setWaitlistPlan}
          interval={interval}
          surface={surface}
        />
      )}
    </div>
  )
}

function errorMessage(
  code: BillingErrorCode,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  switch (code) {
    case 'RATE_LIMITED':
      return t('pricing.errors.rateLimited')
    case 'BILLING_NOT_LIVE':
    case 'BILLING_NOT_CONFIGURED':
      return t('pricing.errors.notLive')
    case 'AUTH_REQUIRED':
      return t('pricing.errors.authRequired')
    case 'NO_CUSTOMER':
      return t('pricing.errors.noCustomer')
    default:
      return t('pricing.errors.generic')
  }
}
